import { FileText } from 'lucide-react';
import { EditableContent } from '../../../edit/EditableContent';
import { LucideIcon } from '../../../icons/LucideIcon';
import { resolveSectionLucideIcon } from '../../../icons/ctaLucideMap';
import { pickBlockField } from '../../../lib/localized';
import { pickMediaUrl } from '../../../lib/media';
import type { BlockRenderProps } from '../../../types';

function pdfEmptyHint(locale: string): string {
  const lang = locale.toLowerCase().slice(0, 2);
  if (lang === 'de') {
    return 'PDF-URL oder Datei in Zone 4 hinterlegen, um die Speisekarte anzuzeigen.';
  }
  if (lang === 'vi') {
    return 'Thêm URL hoặc tệp PDF trong Zone 4 để hiển thị thực đơn.';
  }
  return 'Add a PDF URL or file in the editor to show the menu.';
}

/**
 * PDF Speisekarte viewer — embeds `media.menuPdf` in-page with open-in-new-tab CTA.
 * Label + icon are separate EditableContent hit targets (same pattern as reservation CTA).
 */
export function MenuPdfCard({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const pdfCta = pickBlockField(block.content, 'pdfCta', locale);
  const pdfCtaIconKey = pickBlockField(block.content, 'pdfCtaIcon', locale);
  const menuPdf = pickMediaUrl(block, 'menuPdf');
  const target = isActive ? activeEditTarget : null;
  const viewerTitle = sectionTitle.trim() || 'Speisekarte PDF';
  const showCta = Boolean(menuPdf) || Boolean(editable);
  const ctaIcon = resolveSectionLucideIcon(pdfCtaIconKey, 'pdf');

  return (
    <section
      className={['ws-menu-pdf', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-menu-pdf__inner">
        <header className="ws-menu-pdf__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-pdf__title"
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            fieldKey="sectionSubtitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-pdf__subtitle"
          >
            {sectionSubtitle}
          </EditableContent>
        </header>

        <div className="ws-menu-pdf__viewer-shell">
          {menuPdf ? (
            <iframe
              className="ws-menu-pdf__viewer"
              src={menuPdf}
              title={viewerTitle}
              loading="lazy"
            />
          ) : (
            <div className="ws-menu-pdf__empty" role="status">
              <LucideIcon icon={FileText} size="md" />
              <p className="ws-menu-pdf__empty-text">{pdfEmptyHint(locale)}</p>
            </div>
          )}

          {showCta ? (
            <a
              className="ws-menu-pdf__download"
              href={editable ? '#' : menuPdf || '#'}
              target={menuPdf && !editable ? '_blank' : undefined}
              rel={menuPdf && !editable ? 'noopener noreferrer' : undefined}
              onClick={editable ? (event) => event.preventDefault() : undefined}
            >
              <EditableContent
                as="span"
                fieldKey="pdfCtaIcon"
                editable={editable}
                activeEditTarget={target}
                className="ws-menu-pdf__download-icon"
              >
                <LucideIcon icon={ctaIcon} size="sm" />
              </EditableContent>
              <EditableContent
                as="span"
                fieldKey="pdfCta"
                editable={editable}
                activeEditTarget={target}
                className="ws-menu-pdf__download-label"
              >
                {pdfCta}
              </EditableContent>
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}
